// src/components/practitioners/PractitionerLeistungenSelector.tsx
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

export type TreatmentVariantHierarchyItem = {
    id: string;
    name: string;
    storeNames: string[];
};

export type TreatmentHierarchyItem = {
    id: string;
    name: string;
    variants: TreatmentVariantHierarchyItem[];
};

export type CategoryHierarchyItem = {
    id: number;
    name: string;
    treatments: TreatmentHierarchyItem[];
};

interface PractitionerLeistungenSelectorProps {
    hierarchy: CategoryHierarchyItem[];
    selectedVariantIds: string[]; // IDs from parent form state
    onChange: (selectedIds: string[]) => void; // Callback to update parent form state
    disabled?: boolean;
}

// Helper function to get all variant IDs under a specific node
const getAllVariantIdsInCategory = (category: CategoryHierarchyItem): string[] => {
    return category.treatments.flatMap(treatment => treatment.variants.map(variant => variant.id));
};

const getAllVariantIdsInTreatment = (treatment: TreatmentHierarchyItem): string[] => {
    return treatment.variants.map(variant => variant.id);
};


export function PractitionerLeistungenSelector({
    hierarchy,
    selectedVariantIds,
    onChange,
    disabled = false
}: PractitionerLeistungenSelectorProps) {
    const { userRole } = useUserStore(
        useShallow((state) => ({
            userRole: state.user?.role
        }))
    );
    const shouldDisableAllInputs = userRole !== UserRole.ADMIN;

    // Use a Set for efficient add/delete/lookup of selected IDs internally
    const selectedIdsSet = useMemo(() => new Set(selectedVariantIds), [selectedVariantIds]);

    // --- Checkbox State Calculation ---
    // Calculate the state (checked, indeterminate, unchecked) for category and treatment checkboxes

    const getTreatmentCheckboxState = useCallback((treatment: TreatmentHierarchyItem): 'checked' | 'indeterminate' | 'unchecked' => {
        const allVariantIds = getAllVariantIdsInTreatment(treatment);
        if (allVariantIds.length === 0) return 'unchecked'; // No variants to select

        const selectedCount = allVariantIds.filter(id => selectedIdsSet.has(id)).length;

        if (selectedCount === 0) return 'unchecked';
        if (selectedCount === allVariantIds.length) return 'checked';
        return 'indeterminate';
    }, [selectedIdsSet]);

    const getCategoryCheckboxState = useCallback((category: CategoryHierarchyItem): 'checked' | 'indeterminate' | 'unchecked' => {
        const allVariantIds = getAllVariantIdsInCategory(category);
        if (allVariantIds.length === 0) return 'unchecked';

        const selectedCount = allVariantIds.filter(id => selectedIdsSet.has(id)).length;

        if (selectedCount === 0) return 'unchecked';
        if (selectedCount === allVariantIds.length) return 'checked';
        return 'indeterminate';
    }, [selectedIdsSet]);

    // --- Checkbox Change Handlers ---

    const handleVariantChange = (variantId: string, checked: boolean | 'indeterminate') => {
        const newSelectedIds = new Set(selectedIdsSet);
        if (checked === true) {
            newSelectedIds.add(variantId);
        } else {
            newSelectedIds.delete(variantId);
        }
        onChange(Array.from(newSelectedIds)); // Notify parent
    };

    const handleTreatmentChange = (treatment: TreatmentHierarchyItem, checked: boolean | 'indeterminate') => {
        const variantIds = getAllVariantIdsInTreatment(treatment);
        const newSelectedIds = new Set(selectedIdsSet);
        if (checked === true) { // Check all variants in this treatment
            variantIds.forEach(id => newSelectedIds.add(id));
        } else { // Uncheck all variants in this treatment (handles both false and indeterminate clicks)
            variantIds.forEach(id => newSelectedIds.delete(id));
        }
         onChange(Array.from(newSelectedIds));
    };

    const handleCategoryChange = (category: CategoryHierarchyItem, checked: boolean | 'indeterminate') => {
        const variantIds = getAllVariantIdsInCategory(category);
         const newSelectedIds = new Set(selectedIdsSet);
        if (checked === true) { // Check all variants in this category
            variantIds.forEach(id => newSelectedIds.add(id));
        } else { // Uncheck all variants in this category
            variantIds.forEach(id => newSelectedIds.delete(id));
        }
         onChange(Array.from(newSelectedIds));
    };

    if (!hierarchy || hierarchy.length === 0) {
        return <p className="text-sm text-muted-foreground">Keine Leistungen zur Auswahl verfügbar.</p>;
    }

    return (
        <div className="rounded-md border p-4 max-h-96 overflow-y-auto space-y-2 bg-background">
            <Accordion type="multiple" className="w-full">
                {hierarchy.map((category) => {
                    const categoryState = getCategoryCheckboxState(category);
                    return (
                        <AccordionItem value={`cat-${category.id}`} key={category.id}>
                            {/* Category Level */}
                            <div className="flex items-center space-x-2 mb-1 bg-muted p-2 rounded-t-md border-b">
                                <Checkbox
                                    id={`cat-check-${category.id}`}
                                    checked={categoryState === 'checked' ? true : categoryState === 'unchecked' ? false : 'indeterminate'} // Use calculated state
                                    onCheckedChange={(checked) => handleCategoryChange(category, checked)}
                                    disabled={disabled || shouldDisableAllInputs}
                                    aria-label={`Alle Leistungen in Kategorie ${category.name} auswählen`}
                                />
                                <Label htmlFor={`cat-check-${category.id}`} className="flex-grow font-semibold text-sm cursor-pointer">
                                    <AccordionTrigger className="p-0 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                                        {category.name}
                                    </AccordionTrigger>
                                </Label>
                            </div>

                            <AccordionContent>
                                <Accordion type="multiple" className="w-full pl-4">
                                    {category.treatments.map((treatment) => {
                                        const treatmentState = getTreatmentCheckboxState(treatment);
                                        return (
                                            <AccordionItem value={`treat-${treatment.id}`} key={treatment.id} className="border-b-0">
                                                {/* Treatment Level */}
                                                <div className="flex items-center space-x-2 mb-1 bg-card p-1 rounded-t-md border-b">
                                                     <Checkbox
                                                        id={`treat-check-${treatment.id}`}
                                                        checked={treatmentState === 'checked' ? true : treatmentState === 'unchecked' ? false : 'indeterminate'}
                                                        onCheckedChange={(checked) => handleTreatmentChange(treatment, checked)}
                                                        disabled={disabled || shouldDisableAllInputs}
                                                        aria-label={`Alle Varianten für ${treatment.name} auswählen`}
                                                    />
                                                     <Label htmlFor={`treat-check-${treatment.id}`} className="flex-grow text-xs font-medium cursor-pointer">
                                                        <AccordionTrigger className="p-0 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                                                          {treatment.name}
                                                        </AccordionTrigger>
                                                    </Label>
                                                </div>

                                                <AccordionContent>
                                                     {/* Variant Level */}
                                                    <div className="pl-6 space-y-1 py-1">
                                                        {treatment.variants.map((variant) => (
                                                            <div key={variant.id} className="flex items-center space-x-2">
                                                                <Checkbox
                                                                    id={`var-${variant.id}`}
                                                                    checked={selectedIdsSet.has(variant.id)}
                                                                    onCheckedChange={(checked) => handleVariantChange(variant.id, checked)}
                                                                    disabled={disabled || shouldDisableAllInputs}
                                                                    aria-label={variant.name}
                                                                />
                                                                <Label htmlFor={`var-${variant.id}`} className="text-xs font-normal cursor-pointer">
                                                                    {variant.name} ({variant.storeNames.length === 0 ? "No locations" : variant.storeNames.join(", ")})
                                                                </Label>
                                                            </div>
                                                        ))}
                                                         {treatment.variants.length === 0 && (
                                                            <p className="text-xs text-muted-foreground italic pl-8">Keine Varianten</p>
                                                        )}
                                                    </div>
                                                </AccordionContent>
                                            </AccordionItem>
                                        );
                                    })}
                                     {category.treatments.length === 0 && (
                                        <p className="text-xs text-muted-foreground italic pl-4">Keine Behandlungen in dieser Kategorie</p>
                                    )}
                                </Accordion>
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div>
    );
}